import React, { forwardRef, useId } from "react";
import { useToast } from "@/context/ToastContext";
import { useTranslation } from "react-i18next";
import { Alert } from "@/components/Alert";

export interface InputProps
    extends React.InputHTMLAttributes<HTMLInputElement> {
    label?: string;
    labelClassName?: string;
    error?: string;
    helperText?: string;
    icon?:
        | React.ComponentType<{ size?: number; className?: string }>
        | React.ReactNode;
    endIcon?:
        | React.ComponentType<{ size?: number; className?: string }>
        | React.ReactNode;
    required?: boolean;
    requiredLabel?: boolean;
    disabled?: boolean;
    className?: string;
    type?: string;
    similars?: string[];
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
    (
        {
            id,
            label,
            labelClassName = "",
            error,
            helperText,
            icon,
            endIcon,
            required = false,
            requiredLabel = true,
            disabled = false,
            className = "",
            type = "text",
            similars = [],
            ...props
        },
        ref
    ) => {
        const generatedId = useId();
        const inputId = id || generatedId;

        const { showError } = useToast();
        const { t } = useTranslation("global");

        const renderIcon = (
            iconNode:
                | React.ComponentType<{ size?: number; className?: string }>
                | React.ReactNode,
            isEnd = false
        ) => {
            if (!iconNode) {
                return null;
            }

            const positionClass = isEnd ? "right-3" : "left-3";

            if (React.isValidElement(iconNode)) {
                return (
                    <div
                        className={`absolute ${positionClass} top-1/2 -translate-y-1/2 text-slate-400 dark:text-zinc-400 flex items-center`}
                    >
                        {iconNode}
                    </div>
                );
            }

            const IconComponent = iconNode as React.ComponentType<{
                size?: number;
                className?: string;
            }>;
            return (
                <div
                    className={`absolute ${positionClass} top-1/2 -translate-y-1/2 text-slate-400 dark:text-zinc-400 flex items-center pointer-events-none`}
                >
                    <IconComponent size={15} />
                </div>
            );
        };

        let resolvedType = type;
        let resolvedEndIcon = endIcon;
        let resolvedStep = props.step;

        if (type === "price") {
            resolvedType = "number";
            if (!resolvedEndIcon)
                resolvedEndIcon = (
                    <span className="font-semibold text-slate-400 dark:text-zinc-400">
                        €
                    </span>
                );
            if (!resolvedStep) resolvedStep = "0.01";
        } else if (type === "percentage") {
            resolvedType = "number";
            if (!resolvedEndIcon)
                resolvedEndIcon = (
                    <span className="font-semibold text-slate-400 dark:text-zinc-400">
                        %
                    </span>
                );
            if (!resolvedStep) resolvedStep = "0.01";
        }

        const hasStartIcon = Boolean(icon);
        const hasEndIcon = Boolean(resolvedEndIcon);

        const baseInputClasses = `w-full min-h-[38px] px-3.5 py-2.5 bg-white dark:bg-dark-box border border-slate-200 dark:border-white/20 rounded-lg text-xs text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-zinc-500 focus:outline-none outline-none transition-all shadow-xs disabled:opacity-50 disabled:cursor-not-allowed ${
            hasStartIcon ? "pl-9" : ""
        } ${hasEndIcon ? "pr-9" : ""} ${
            error
                ? "!border-rose-400 !bg-rose-50/50 dark:!bg-rose-950/20 text-rose-900 dark:text-rose-100"
                : ""
        } ${className}`;

        const duplicateWarning = React.useMemo(() => {
            const val =
                props.value === null || props.value === undefined
                    ? ""
                    : String(props.value);
            if (!val || val.trim().length < 3 || !similars?.length) return null;
            const lowerVal = val.trim().toLowerCase();
            const match = similars.find((s) => {
                if (!s) return false;
                const lowerS = s.toLowerCase();
                return (
                    lowerS === lowerVal ||
                    (lowerS.length > 3 &&
                        (lowerS.includes(lowerVal) ||
                            lowerVal.includes(lowerS)))
                );
            });
            return match;
        }, [props.value, similars]);

        return (
            <div className="w-full">
                {label && (
                    <label
                        htmlFor={inputId}
                        className={`block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1 ${labelClassName}`}
                    >
                        {label}{" "}
                        {required && requiredLabel && (
                            <span className="text-rose-600">*</span>
                        )}
                    </label>
                )}

                <div className="relative flex items-center">
                    {renderIcon(icon, false)}

                    <input
                        {...props}
                        id={inputId}
                        ref={ref}
                        type={resolvedType}
                        step={resolvedStep}
                        required={required}
                        disabled={disabled}
                        aria-invalid={Boolean(error) || undefined}
                        aria-describedby={
                            [
                                props["aria-describedby"],
                                error
                                    ? `${inputId}-error`
                                    : helperText
                                      ? `${inputId}-help`
                                      : "",
                            ]
                                .filter(Boolean)
                                .join(" ") || undefined
                        }
                        className={baseInputClasses}
                        onInvalid={(e) => {
                            e.preventDefault();
                            if (
                                required &&
                                !(e.target as HTMLInputElement).value
                            ) {
                                showError(t("errors.field_required"));
                            }
                            if (props.onInvalid) {
                                props.onInvalid(e);
                            }
                        }}
                    />

                    {renderIcon(resolvedEndIcon, true)}
                </div>

                {error && (
                    <p
                        id={`${inputId}-error`}
                        className="mt-1 text-xs text-rose-700 dark:text-rose-400 font-medium"
                    >
                        {error}
                    </p>
                )}

                {!error && helperText && (
                    <p
                        id={`${inputId}-help`}
                        className="mt-1 text-xs text-slate-500 dark:text-zinc-400"
                    >
                        {helperText}
                    </p>
                )}

                {duplicateWarning && (
                    <div className="mt-2">
                        <Alert variant="warning" icon={null}>
                            {t("common.similar_element_warning", {
                                name: duplicateWarning,
                            })}
                        </Alert>
                    </div>
                )}
            </div>
        );
    }
);

Input.displayName = "Input";

export default Input;
